import { useState } from 'react';
import type { ApplicationDetail, FeedbackItem, Recommendation } from '../api/client';
import { useEditFeedback, useSubmitFeedback } from '../api/applications';

const RECS: { value: Recommendation; label: string }[] = [
  { value: 'STRONG_HIRE', label: 'Strong hire' },
  { value: 'HIRE', label: 'Hire' },
  { value: 'NO_HIRE', label: 'No hire' },
  { value: 'STRONG_NO_HIRE', label: 'Strong no hire' },
];
const RECO_LABEL: Record<Recommendation, string> = {
  STRONG_HIRE: 'Strong hire',
  HIRE: 'Hire',
  NO_HIRE: 'No hire',
  STRONG_NO_HIRE: 'Strong no hire',
};

function Card({ f }: { f: FeedbackItem }) {
  return (
    <div className="feedback-card">
      <div className="feedback-head">
        <strong>{f.interviewer.displayName}</strong>
        <span className="muted small">{'★'.repeat(f.rating)}{'☆'.repeat(5 - f.rating)} · {RECO_LABEL[f.recommendation]}</span>
      </div>
      {f.strengths && <p className="small"><span className="meta-label">Strengths</span> {f.strengths}</p>}
      {f.concerns && <p className="small"><span className="meta-label">Concerns</span> {f.concerns}</p>}
      {f.notes && <p className="small"><span className="meta-label">Notes</span> {f.notes}</p>}
    </div>
  );
}

export default function FeedbackPanel({ application }: { application: ApplicationDetail }) {
  const submit = useSubmitFeedback(application.id);
  const edit = useEditFeedback(application.id);
  const mine = application.myFeedback;

  const [rating, setRating] = useState(mine?.rating ?? 3);
  const [recommendation, setRecommendation] = useState<Recommendation>(mine?.recommendation ?? 'HIRE');
  const [strengths, setStrengths] = useState(mine?.strengths ?? '');
  const [concerns, setConcerns] = useState(mine?.concerns ?? '');
  const [notes, setNotes] = useState(mine?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const showForm = application.canSubmitFeedback || application.canEditFeedback;
  const busy = submit.isPending || edit.isPending;

  const send = () => {
    setError(null);
    const payload = {
      rating,
      recommendation,
      strengths: strengths.trim() || undefined,
      concerns: concerns.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    const mutation = application.canEditFeedback ? edit : submit;
    mutation.mutate(payload, { onError: (e) => setError(e instanceof Error ? e.message : 'Failed') });
  };

  return (
    <div>
      {application.feedback && application.feedback.length > 0 && (
        <div className="feedback-list">{application.feedback.map((f) => <Card key={f.id} f={f} />)}</div>
      )}
      {!application.feedback && mine && !showForm && (
        <div className="feedback-list"><Card f={mine} /></div>
      )}

      {showForm && (
        <div className="form" style={{ maxWidth: 560 }}>
          {application.canEditFeedback && <p className="muted small">Edit your feedback:</p>}
          {error && <div className="banner-error">{error}</div>}
          <div className="field-row">
            <label className="field"><span>Rating</span>
              <select value={rating} onChange={(e) => setRating(Number(e.target.value))}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} {'★'.repeat(n)}</option>)}
              </select>
            </label>
            <label className="field"><span>Recommendation</span>
              <select value={recommendation} onChange={(e) => setRecommendation(e.target.value as Recommendation)}>
                {RECS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>
          <label className="field"><span>Strengths</span><textarea rows={2} value={strengths} onChange={(e) => setStrengths(e.target.value)} /></label>
          <label className="field"><span>Concerns</span><textarea rows={2} value={concerns} onChange={(e) => setConcerns(e.target.value)} /></label>
          <label className="field"><span>Notes</span><textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
          <div className="form-actions">
            <button className="btn-primary" disabled={busy} onClick={send}>
              {busy ? 'Saving…' : application.canEditFeedback ? 'Update feedback' : 'Submit feedback'}
            </button>
          </div>
        </div>
      )}

      {!application.feedback && !mine && !showForm && <p className="muted">No feedback yet.</p>}
    </div>
  );
}
